"use client";

import { useMemo, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import ConfigSection from "./ConfigSection";
import IndicatorParameters from "./IndicatorParameters";
import StyleConfig from "./StyleConfig";
import type { IndicatorStyleSchema } from "@/lib/chart-indicators/registry";
import { getIndicatorDefinition } from "@/lib/chart-indicators/registry";

interface BaseIndicatorConfigProps {
  indicator: {
    indicatorId: string;
    name: string;
    parameters?: Array<{
      name: string;
      type: "number" | "color" | "select";
      defaultValue: number | string;
      options?: string[];
      min?: number;
      max?: number;
      step?: number;
    }>;
    settings?: {
      values?: Record<string, number | string>;
      styles?: Record<string, { color: string; style: "solid" | "dashed" | "dotted" }>;
      styleSettings?: Record<string, { color: string; style: "solid" | "dashed" | "dotted" }>;
    };
  };
  onBack: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onApply: (...args: any[]) => void;
  /** Explicit edit mode; settings.values is always populated so cannot be used to infer it. */
  isEditing?: boolean;
  styleConfigs?: IndicatorStyleSchema[];
  defaultExpandedStyle?: string;
  onShowTimeframes?: () => void;
  onShowSelector?: (paramName: string) => void;
  selectedTimeframes?: string[];
  showFillToggle?: boolean;
  showColorSlider?: boolean;
  additionalParameters?: Array<{
    name: string;
    type: "select";
    value: string;
  }>;
}

export default function BaseIndicatorConfig({
  indicator,
  onApply,
  isEditing = false,
  styleConfigs: styleConfigsProp,
}: BaseIndicatorConfigProps) {
  const def = getIndicatorDefinition(indicator.indicatorId);
  const styleConfigs: IndicatorStyleSchema[] =
    styleConfigsProp ?? def?.styles ?? [];

  const [values, setValues] = useState<Record<string, number | string>>(() => {
    if (indicator.settings?.values) {
      return { ...indicator.settings.values };
    }
    const initialValues: Record<string, number | string> = {};
    indicator.parameters?.forEach((param) => {
      initialValues[param.name] = param.defaultValue;
    });
    return initialValues;
  });

  const readStyles = () =>
    indicator.settings?.styles ?? indicator.settings?.styleSettings;

  const [styleColors, setStyleColors] = useState<Record<string, string>>(() => {
    const colors: Record<string, string> = {};
    const saved = readStyles();
    styleConfigs.forEach((config) => {
      colors[config.id] = saved?.[config.id]?.color ?? config.defaultColor;
    });
    return colors;
  });

  const [styleTypes, setStyleTypes] = useState<
    Record<string, "solid" | "dashed" | "dotted">
  >(() => {
    const types: Record<string, "solid" | "dashed" | "dotted"> = {};
    const saved = readStyles();
    styleConfigs.forEach((config) => {
      types[config.id] = saved?.[config.id]?.style ?? config.defaultStyle;
    });
    return types;
  });

  const paramSummary = useMemo(() => {
    const parts: string[] = [];
    if (values.Period != null) parts.push(`Period ${values.Period}`);
    if (values.Method != null) parts.push(String(values.Method));
    if (values["Apply to"] != null) parts.push(String(values["Apply to"]));
    return parts.join(" · ");
  }, [values]);

  const handleApply = () => {
    const styles: Record<
      string,
      { color: string; style: "solid" | "dashed" | "dotted" }
    > = {};
    styleConfigs.forEach((config) => {
      styles[config.id] = {
        color: styleColors[config.id],
        style: styleTypes[config.id],
      };
    });

    const { settings: _settings, ...rest } = indicator as {
      settings?: unknown;
      [key: string]: unknown;
    };

    onApply({
      ...rest,
      settings: { values, styles },
    });
  };

  const handleReset = () => {
    const defaultValues: Record<string, number | string> = {};
    indicator.parameters?.forEach((param) => {
      defaultValues[param.name] = param.defaultValue;
    });
    setValues(defaultValues);

    const newColors: Record<string, string> = {};
    const newTypes: Record<string, "solid" | "dashed" | "dotted"> = {};
    styleConfigs.forEach((config) => {
      newColors[config.id] = config.defaultColor;
      newTypes[config.id] = config.defaultStyle;
    });
    setStyleColors(newColors);
    setStyleTypes(newTypes);
  };

  return (
    <div className="flex flex-col min-h-0 flex-1 bg-background">
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {paramSummary ? (
          <p className="text-xs text-muted-foreground px-1">{paramSummary}</p>
        ) : null}

        {(indicator.parameters?.length ?? 0) > 0 && (
          <ConfigSection
            title="Parameters"
            description="Calculation inputs for this indicator"
          >
            <IndicatorParameters
              parameters={indicator.parameters}
              values={values}
              onValueChange={(name, value) =>
                setValues((prev) => ({ ...prev, [name]: value }))
              }
            />
          </ConfigSection>
        )}

        {styleConfigs.length > 0 && (
          <ConfigSection
            title="Appearance"
            description="Colors and line styles on the chart"
          >
            {styleConfigs.map((config) => (
              <StyleConfig
                key={config.id}
                label={config.label}
                color={styleColors[config.id]}
                style={styleTypes[config.id]}
                onColorChange={(color) =>
                  setStyleColors((prev) => ({ ...prev, [config.id]: color }))
                }
                onStyleChange={(style) =>
                  setStyleTypes((prev) => ({ ...prev, [config.id]: style }))
                }
              />
            ))}
          </ConfigSection>
        )}
      </div>

      <div className="shrink-0 border-t border-border bg-background/95 backdrop-blur-sm px-3 py-3">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1 gap-1.5"
            onClick={handleReset}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </Button>
          <Button type="button" className="flex-1" onClick={handleApply}>
            {isEditing ? "Save changes" : "Add to chart"}
          </Button>
        </div>
      </div>
    </div>
  );
}
